import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import type { AppLimits } from "../lib/types";

const FALLBACK: AppLimits = {
  currency: "EUR",
  premiumClicksPerCent: 10,
  quickAmountsEur: [5, 10, 25, 50, 100],
};

/** Parses "5,10,25,50,100" into [5, 10, 25, 50, 100], dropping anything malformed (non-positive/non-integer/unparsable) rather than letting one bad value break the whole row of buttons. */
function parseQuickAmounts(raw: string | undefined): number[] {
  if (!raw) return FALLBACK.quickAmountsEur;
  const parsed = raw
    .split(",")
    .map((piece) => Number(piece.trim()))
    .filter((n) => Number.isFinite(n) && Number.isInteger(n) && n > 0);
  return parsed.length > 0 ? parsed : FALLBACK.quickAmountsEur;
}

/** Reads the frontend-safe subset of app_config (via the public_app_config view) once on mount. */
export function useAppConfig(): { limits: AppLimits; loading: boolean } {
  const [limits, setLimits] = useState<AppLimits>(FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error } = await supabase.from("public_app_config").select("key, value");
      if (!active) return;
      if (error) {
        console.error("failed to load public_app_config", error);
      } else if (data) {
        const map = new Map((data as { key: string; value: string }[]).map((row) => [row.key, row.value]));
        setLimits({
          currency: map.get("currency") ?? FALLBACK.currency,
          premiumClicksPerCent: Number(map.get("premium_clicks_per_cent") ?? FALLBACK.premiumClicksPerCent),
          quickAmountsEur: parseQuickAmounts(map.get("quick_amounts_eur")),
        });
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  return { limits, loading };
}
